<script setup lang="ts">
import type { CustomerDetails } from "@/schemas/customer";

const props = defineProps<{ customerDetails: CustomerDetails }>();

const current = ref<CustomerDetails>({ ...props.customerDetails });

const { countryOptions, stateOptions } = useCountries(computed(() => ({ country: current.value.country, state: current.value.state })));

type BaseField = {
  name: keyof CustomerDetails;
  label: string;
  required?: boolean;
  class?: string;
};
type InputField = BaseField & {
  type: "text" | "email" | "tel";
};
type SelectField = BaseField & {
  type: "select";
  options: { label: string; value: string }[];
};
type CustomerField = InputField | SelectField;

const fields = computed<CustomerField[]>(() => {
  const baseFields: CustomerField[] = [
    {
      name: "first_name",
      label: "Prénom",
      type: "text",
      required: true,
    },
    {
      name: "last_name",
      label: "Nom",
      type: "text",
      required: true,
    },
    {
      name: "company",
      label: "Nom de l’entreprise",
      type: "text",
      class: "lg:col-span-2",
    },
    {
      name: "address_1",
      label: "Numéro et nom de rue",
      type: "text",
      required: true,
    },
    {
      name: "address_2",
      label: "Adresse (suite)",
      type: "text",
    },
    {
      name: "city",
      label: "Ville",
      type: "text",
      required: true,
    },
    {
      name: "postcode",
      label: "Code postal",
      type: "text",
      required: true,
    },
    {
      name: "country",
      label: "Pays",
      type: "select",
      options: countryOptions.value,
      required: true,
      class: stateOptions.value.length ? "" : "lg:col-span-2",
    },
    {
      name: "state",
      label: "Province",
      type: "select",
      options: stateOptions.value,
      required: true,
    },
  ];
  if (props.customerDetails.prefix === "billing") {
    baseFields.push(
      {
        name: "phone",
        label: "Téléphone",
        type: "tel",
        required: true,
        class: "lg:col-span-2",
      },
      {
        name: "email",
        label: "Courriel",
        type: "email",
        required: true,
        class: "lg:col-span-2",
      },
    );
  }
  return baseFields;
});
</script>

<template>
  <div class="grid gap-4">
    <template v-for="field in fields" :key="field.name">
      <div v-if="field.type !== 'select' || field.options.length" :class="field.class">
        <UiField
          :name="`${customerDetails.prefix}_${field.name}`"
          :label="field.label"
          :required="field.required"
        >
          <input
            v-if="field.type !== 'select'"
            :id="`${customerDetails.prefix}_${field.name}`"
            v-model="current[field.name]"
            :type="field.type"
            :name="`${customerDetails.prefix}_${field.name}`"
            class="form-input"
            :aria-required="field.required" :required="field.required"
          >
          <select
            v-else-if="field.type === 'select'"
            :id="`${customerDetails.prefix}_${field.name}`"
            v-model="current[field.name]"
            :name="`${customerDetails.prefix}_${field.name}`"
            class="form-select"
            :aria-required="field.required" :required="field.required"
          >
            <option v-for="option in field.options" :key="option.value" :value="option.value">
              {{ option.label }}
            </option>
          </select>
        </UiField>
      </div>
    </template>
  </div>
</template>
